<template>
  <div v-if="isLoading" class="flex flex-col items-center justify-center flex-1 px-6 py-14 sm:px-14">
    <span class="i-heroicons-arrow-path text-slate-500 size-8 animate-spin" />
    <p class="text-sm text-center text-slate-500 pt-4">
      Loading...
    </p>
  </div>

  <div
    v-else-if="profile.id && !isLoading && !isError"
    class="settings-edit-user"
  >
    <h2 class="text-lg font-semibold text-slate-900 mb-6 flex items-center">
      <router-link :to="{ name: 'UserSettings' }" class="i-heroicons-arrow-left text-slate-500 size-5 mr-2" />

      Edit User
    </h2>

    <div class="mb-4 flex flex-col md:flex-row gap-4">
      <div class="flex flex-col justify-center w-full md:w-1/3">
        <p class="mb-4 max-w-[100px]">
          <BaseAvatar
            :image="profile.avatar"
            :alt="profile.name"
          />
        </p>
        <h4 class="text-slate-900 font-bold text-lg mb-2">
          {{ profile.name }}
        </h4>
        <p class="text-slate-500 text-sm mb-2">
          {{ profile.email }}
        </p>
      </div>

      <div class="flex flex-col w-full md:w-2/3">
        <form @submit.stop.prevent="form.handleSubmit()">
          <div class="mb-4">
            <label class="text-slate-900 font-semibold text-sm">Roles</label>
            <p class="text-slate-500 text-sm">Select the roles for this user</p>
            <div class="mt-2 flex gap-4 flex-wrap">
              <BaseCheckbox
                v-for="(role, index) in roles"
                :key="index"
                v-model="formUserRoles"
                :label="role.text"
                :value="role.value"
                :disabled="role.disabled"
              />
            </div>
          </div>

          <form.Subscribe>
            <template #default="{ isSubmitting }">
              <BaseButton
                variant="primary"
                type="submit"
                class="w-full"
                :disabled="isSubmitting"
                :class="isSubmitting ? 'opacity-50 cursor-not-allowed' : ''"
              >
                <div class="flex justify-center items-center gap-2">
                  <span>Update User</span>
                  <span
                    v-if="isSubmitting"
                    class="i-heroicons-arrow-path animate-spin"
                  />
                </div>
              </BaseButton>
            </template>
          </form.Subscribe>
        </form>
      </div>
    </div>
  </div>

  <EmptyState v-else-if="!isLoading && isError && !showError">
    <template #icon>
      <span class="i-heroicons-exclamation-circle text-red-500 size-8"></span>
    </template>
    <template #title>
      <h2 class="text-lg font-semibold text-center text-slate-900">Error</h2>
    </template>
    <template #description>
      <p class="text-sm text-center text-slate-500 pt-4">
        An error occurred while trying to load the user data
      </p>
    </template>
  </EmptyState>
</template>

<script lang="ts" setup>
import EmptyState from '@/components/app/EmptyState.vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import { UserClass } from '@/models/user'
import FirebaseService from '@/services/FirebaseService'
import { useNotification } from '@kyvg/vue3-notification'
import { useForm } from '@tanstack/vue-form'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { httpsCallable } from 'firebase/functions'
import { ref, watch } from 'vue'
import { useRoute } from 'vue-router'


const $route = useRoute()
const id = $route.params.uid as string
const { notify } = useNotification()
const showError = ref(false)

const user = ref({})
const profile = ref(new UserClass())
const formUserRoles = ref([])
const roles = ref([
  { text: 'Employee', value: 'employee', disabled: true },
  { text: 'Sales', value: 'sales' },
  { text: 'Finance', value: 'finance' },
  { text: 'Estimator', value: 'estimator' },
  { text: 'Admin', value: 'admin' }
])

const { data: userData, isLoading, isError } = useQuery({
  queryKey: ['userData', id],
  queryFn: async () => {
    try {
      const functions = await FirebaseService.functions()
      const userGet = httpsCallable(functions, 'user-getV2')
      const result = await userGet({ uid: id })
      const data = result.data as any

      return data
    } catch (error) {
      console.log(error, 'Error getting user data!')
      notify({
        type: 'error',
        title: 'Error',
        text: 'Error getting user data!'
      })
      return []
    }
  }
})

watch(isLoading, (loading) => {
  if (!loading && userData.value && !isError.value) {
    const data = userData.value as any
    if (data.status === 'successful') {
      user.value = data.user
      profile.value = data.profile
      formUserRoles.value = Object.keys(data.user.customClaims)
    } else {
      showError.value = true
    }
  }
}, { immediate: true })

const form = useForm({
  defaultValues: {
    roles: formUserRoles.value
  },
  onSubmit: async () => {
    await onUpdateUser()
  }
})

const { mutateAsync: onUpdateUser } = useMutation({
  mutationFn: async () => {
    const functions = await FirebaseService.functions()
    const update = httpsCallable(functions, 'user-editV2')
    const result = await update({ uid: id, roles: formUserRoles.value })
    const data = result.data as any

    if (data.status !== 'successful') {
      throw new Error('Could not update user!')
    }
    return data
  },
  onSuccess: (data) => {
    if (data.status === 'successful') {
      notify({
        type: 'success',
        title: 'Success',
        text: 'User updated successfully!'
      })
      user.value = data.user
    }
  },
  onError: () => {
    notify({
      type: 'error',
      title: 'Error',
      text: 'Could not update user!'
    })
  }
})
</script>
